import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Container,
    CircularProgress,
    MenuItem,
    Select,
    InputLabel,
    FormControl
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Servicios (simulados basados en tu estructura)
const api = axios.create({
    baseURL: 'https://dymesenergy.gst-panel.com'
});

const DepartamentoService = {
    obtenerTodos: () => api.get('/get/obtener_departamentos.php')
        .then(response => ({
            data: Array.isArray(response.data) ? response.data : response.data?.data || []
        }))
};

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    marginTop: theme.spacing(4),
    borderRadius: '12px',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderRadius: '8px',
    fontWeight: 'bold',
    textTransform: 'none',
    fontSize: '16px',
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: '100%',
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
    },
}));

const MunicipioForm = () => {
    const [nombre, setNombre] = useState('');
    const [idDepartamento, setIdDepartamento] = useState('');
    const [departamentos, setDepartamentos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDepartamentos, setLoadingDepartamentos] = useState(true);
    const { enqueueSnackbar } = useSnackbar();
    const [kwp, setKwp] = useState('');


    // Cargar departamentos al iniciar
    useEffect(() => {
        const cargarDepartamentos = async () => {
            try {
                const response = await DepartamentoService.obtenerTodos();
                setDepartamentos(response.data);
            } catch (error) {
                enqueueSnackbar('Error al cargar departamentos', {
                    variant: 'error',
                    autoHideDuration: 5000
                });
                console.error('Error cargando departamentos:', error);
            } finally {
                setLoadingDepartamentos(false);
            }
        };

        cargarDepartamentos();
    }, [enqueueSnackbar]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!nombre.trim()) {
            enqueueSnackbar('El nombre del municipio es requerido', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        if (!idDepartamento) {
            enqueueSnackbar('Debe seleccionar un departamento', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }
        if (!kwp || isNaN(kwp) || parseFloat(kwp) <= 0) {
            enqueueSnackbar('Debe ingresar un valor numérico válido para Kwp', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        setLoading(true);
        if (!kwp || isNaN(kwp) || parseFloat(kwp) <= 0) {
            enqueueSnackbar('Debe ingresar un valor numérico válido para Kwp', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        try {
            const response = await api.post('/agregar/guardar_municipios.php', {
                Nombre: nombre,
                Id_departamento: idDepartamento,
                Kwp: parseFloat(kwp)

            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            if (response.data.success) {
                enqueueSnackbar(`Municipio "${nombre}" agregado con éxito al departamento`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right'
                    }
                });
                setNombre('');
                setIdDepartamento('');
                setKwp('');
            } else {
                throw new Error(response.data.message || 'Error al crear municipio');
            }
        } catch (error) {
            console.error('Error:', error);
            let errorMessage = 'Ocurrió un problema al agregar el municipio';

            if (error.response) {
                errorMessage = error.response.data.message || errorMessage;
            } else if (error.request) {
                errorMessage = 'No se pudo conectar con el servidor';
            }

            enqueueSnackbar(errorMessage, {
                variant: 'error',
                autoHideDuration: 5000,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                }
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="md">
            <StyledPaper elevation={3}>
                <Typography variant="h4" component="h1" gutterBottom sx={{
                    fontWeight: 'bold',
                    color: 'primary.main',
                    marginBottom: 3
                }}>
                    Agregar Nuevo Municipio
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                    <StyledTextField
                        fullWidth
                        label="Nombre del Municipio"
                        variant="outlined"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        disabled={loading}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <StyledTextField
                        fullWidth
                        label="Kwp estimado"
                        variant="outlined"
                        type="number"
                        value={kwp}
                        onChange={(e) => setKwp(e.target.value)}
                        disabled={loading}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />

                    <StyledFormControl variant="outlined" disabled={loadingDepartamentos || loading}>
                        <InputLabel id="departamento-label">Departamento</InputLabel>
                        <Select
                            labelId="departamento-label"
                            value={idDepartamento}
                            onChange={(e) => setIdDepartamento(e.target.value)}
                            label="Departamento"
                            required
                        >
                            <MenuItem value="">
                                <em>Seleccione un departamento</em>
                            </MenuItem>
                            {departamentos.map((departamento) => (
                                <MenuItem key={departamento.Id} value={departamento.Id}>
                                    {departamento.Nombre}
                                </MenuItem>
                            ))}
                        </Select>
                    </StyledFormControl>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <StyledButton
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading || loadingDepartamentos}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {loading ? 'Guardando...' : 'Guardar Municipio'}
                        </StyledButton>
                    </Box>
                </Box>
            </StyledPaper>
        </Container>
    );
};

export default MunicipioForm;